import { useEffect, useMemo, useState } from "react";
import { SmartTable } from "@/components/SmartTable";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Download, Link2, Loader2, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AssocierDepotDialog } from "@/components/AssocierDepotDialog";
import { SuggestionCard } from "@/components/SuggestionCard";
import { api } from "@/lib/api";

const RELEVES = ["banque", "airbnb", "booking"];

const lireErreur = async (e) => {
  try { return JSON.parse(await e.response.data.text()).detail; } catch { return "Aperçu impossible"; }
};

const sansAccent = (s) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

const Surligne = ({ v, q }) => {
  const i = q ? v.toLowerCase().indexOf(q.toLowerCase()) : -1;
  if (i < 0) { return v; }
  return <>{v.slice(0, i)}<mark className="rounded-sm bg-amber-200 px-0.5 text-slate-900">{v.slice(i, i + q.length)}</mark>{v.slice(i + q.length)}</>;
};

const BoutonLien = ({ lien, i, onOuvrir, onAssocier, sel, onCoche }) => {
  if (!lien) { return null; }
  return (
    <div className="flex items-center gap-1">
      {lien.resa && <Checkbox checked={sel.has(lien.resa)} onCheckedChange={(v) => onCoche(lien.resa, v === true)} className="mr-1" data-testid={`apercu-coche-${i}`} />}
      <button type="button" onClick={() => onOuvrir(lien)} data-testid={`apercu-lien-${i}`}
        title={lien.depot ? "Ouvrir ce dépôt sur la page Rapprochement" : "Réservation sans dépôt : l'ouvrir sur la page Rapprochement"}
        className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-800 transition-colors hover:bg-emerald-100">
        {lien.depot ? "Rapprochement" : "Sans dépôt"}<ArrowUpRight className="h-3 w-3" />
      </button>
      {lien.resa && (
        <button type="button" onClick={() => onAssocier([lien.resa])} data-testid={`apercu-associer-${i}`} title="Associer cette réservation à un dépôt"
          className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800 transition-colors hover:bg-amber-100">
          <Link2 className="h-3 w-3" />Associer
        </button>
      )}
    </div>
  );
};

const TableApercu = ({ t, items, q, onOuvrir, onAssocier, sel, onCoche }) => (
  <div className="h-full overflow-auto rounded-md border border-slate-200" data-testid="apercu-table">
    <SmartTable id={`apercu-${t.type || "fichier"}`} className="min-w-full border-collapse text-xs">
      <thead className="sticky top-0 z-10 bg-slate-100">
        <tr>
          {t.liens && <th className="border-b border-slate-200 px-2 py-1.5" />}
          {t.colonnes.map((c, i) => <th key={i} className="whitespace-nowrap border-b border-slate-200 px-2 py-1.5 text-left font-semibold text-slate-600">{c}</th>)}
        </tr>
      </thead>
      <tbody>
        {items.map(({ l, lien, i }) => (
          <tr key={i} className="odd:bg-white even:bg-slate-50/70 hover:bg-sky-50" data-testid="apercu-ligne">
            {t.liens && <td className="border-b border-slate-100 px-2 py-1"><BoutonLien lien={lien} i={i} onOuvrir={onOuvrir} onAssocier={onAssocier} sel={sel} onCoche={onCoche} /></td>}
            {l.map((v, j) => <td key={j} className="whitespace-nowrap border-b border-slate-100 px-2 py-1 font-mono text-slate-700"><Surligne v={v} q={q} /></td>)}
          </tr>
        ))}
      </tbody>
    </SmartTable>
    {!items.length && <p className="p-6 text-center text-sm text-slate-400" data-testid="apercu-aucun-resultat">Aucune ligne ne contient « {q} »</p>}
  </div>
);

const RechercheApercu = ({ q, setQ, n }) => (
  <div className="relative w-full sm:w-80">
    <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
    <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher une opération, un code, un montant…"
      className="h-8 w-full rounded-md border border-slate-200 bg-white pl-8 pr-7 text-sm outline-none transition-shadow focus:ring-2 focus:ring-sky-300" data-testid="apercu-recherche" />
    {q && <button type="button" onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700" data-testid="apercu-recherche-effacer"><X className="h-3.5 w-3.5" /></button>}
    {q && <span className="absolute -bottom-4 left-0 text-[11px] text-slate-500" data-testid="apercu-resultats">{n} résultat{n > 1 ? "s" : ""}</span>}
  </div>
);

const ChoixVue = ({ vue, setVue }) => (
  <div className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-0.5 text-xs" data-testid="apercu-vues">
    {[["operations", "Opérations"], ["fichier", "Fichier d'origine"]].map(([v, label]) => (
      <button key={v} type="button" onClick={() => setVue(v)} data-testid={`apercu-vue-${v}`}
        className={`rounded px-2.5 py-1 transition-colors ${vue === v ? "bg-white font-medium text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>{label}</button>
    ))}
  </div>
);

const BarreSelection = ({ n, onVider, onAssocier }) => (
  <div className="flex flex-wrap items-center gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5" data-testid="apercu-selection-bar">
    <span className="text-sm text-amber-900" data-testid="apercu-selection-count">{n} réservation{n > 1 ? "s" : ""} sans dépôt sélectionnée{n > 1 ? "s" : ""}</span>
    <span className="text-xs text-amber-800/80">La sélection est gardée si vous ouvrez un autre relevé</span>
    <Button variant="ghost" size="sm" className="h-7" onClick={onVider} data-testid="apercu-selection-vider">Désélectionner</Button>
    <Button size="sm" className="ml-auto h-7 bg-emerald-700 hover:bg-emerald-800" onClick={onAssocier} data-testid="apercu-selection-associer">
      <Link2 className="mr-1.5 h-3.5 w-3.5" />Associer au même dépôt
    </Button>
  </div>
);

const infoTable = (t, fichier) => {
  if (!t) { return " "; }
  const base = `${t.total} ${t.unite || "ligne"}(s)${t.total > t.lignes.length ? `, ${t.lignes.length} premières affichées` : ""}`;
  if (t.origine !== "donnees") { return base; }
  return `${base} · ${fichier?.telechargeable ? "opérations enregistrées lors de l'import" : "fichier d'origine non conservé : données enregistrées lors de l'import"}`;
};

export const ApercuFichier = ({ fichier, onClose, onDownload, sel, setSel }) => {
  const navigate = useNavigate();
  const [etat, setEtat] = useState(null);
  const [q, setQ] = useState("");
  const [vue, setVue] = useState("operations");
  const [associer, setAssocier] = useState(null);
  const [version, setVersion] = useState(0);
  const [suggestions, setSuggestions] = useState([]);
  const deuxVues = !!fichier && RELEVES.includes(fichier.type) && fichier.telechargeable;

  useEffect(() => { setVue("operations"); setQ(""); }, [fichier]);
  useEffect(() => {
    if (!fichier || fichier.type === "banque" || !RELEVES.includes(fichier.type)) { setSuggestions([]); return; }
    api.get("/associations/suggestions").then((r) => setSuggestions(r.data)).catch(() => setSuggestions([]));
  }, [fichier, version]);
  const coche = (id, v) => setSel((s) => { const n = new Set(s); v ? n.add(id) : n.delete(id); return n; });

  useEffect(() => {
    if (!fichier) { return undefined; }
    let url = null;
    setEtat((e) => (version && e?.table ? e : { chargement: true }));
    api.get(`/fichiers/${fichier.id}/apercu`, { params: { vue }, responseType: "blob" }).then(async (r) => {
      if (r.data.type === "application/pdf") {
        url = URL.createObjectURL(r.data);
        setEtat({ pdf: url });
      } else if (r.data.type.startsWith("image/")) {
        url = URL.createObjectURL(r.data);
        setEtat({ image: url });
      } else {
        setEtat({ table: JSON.parse(await r.data.text()) });
      }
    }).catch(async (e) => setEtat({ erreur: await lireErreur(e) }));
    return () => { if (url) { URL.revokeObjectURL(url); } };
  }, [fichier, vue, version]);

  const t = etat?.table;
  const items = useMemo(() => {
    if (!t) { return []; }
    const n = sansAccent(q.trim());
    const tous = t.lignes.map((l, i) => ({ l, lien: t.liens?.[i] || null, i }));
    return n ? tous.filter(({ l }) => l.some((v) => sansAccent(v).includes(n))) : tous;
  }, [t, q]);

  const ouvrir = (lien) => {
    const p = new URLSearchParams(lien.depot ? { depot: lien.depot, date: lien.date } : { resa: lien.resa, date: lien.date });
    onClose();
    navigate(`/rapprochement?${p}`);
  };

  const resasFichier = new Set((t?.liens || []).filter((l) => l?.resa).map((l) => l.resa));
  const suggFichier = suggestions.filter((s) => s.reservations.some((r) => resasFichier.has(r.id)));
  const apresAssociation = () => { setSel(new Set()); setVersion((v) => v + 1); };

  return (
    <Dialog open={!!fichier} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="flex h-[85vh] max-w-6xl flex-col gap-3" data-testid="apercu-dialog">
        <DialogHeader className="pr-8">
          <DialogTitle className="break-all text-base" data-testid="apercu-titre">{fichier?.nom}</DialogTitle>
          <DialogDescription data-testid="apercu-info">{infoTable(t, fichier)}</DialogDescription>
        </DialogHeader>
        {(t || deuxVues) && (
          <div className="flex flex-wrap items-center gap-3 pb-2">
            {t && <RechercheApercu q={q} setQ={setQ} n={items.length} />}
            {deuxVues && <div className="ml-auto"><ChoixVue vue={vue} setVue={setVue} /></div>}
          </div>
        )}
        {suggFichier.length > 0 && (
          <div className="max-h-40 space-y-1.5 overflow-y-auto" data-testid="apercu-suggestions">
            <p className="text-xs font-medium text-sky-800">Groupe{suggFichier.length > 1 ? "s" : ""} suggéré{suggFichier.length > 1 ? "s" : ""} pour ce relevé (total à 5 % près du dépôt)</p>
            {suggFichier.map((s) => <SuggestionCard key={s.depot.id} s={s} onDone={apresAssociation} compact />)}
          </div>
        )}
        {sel.size > 0 && t && <BarreSelection n={sel.size} onVider={() => setSel(new Set())} onAssocier={() => setAssocier([...sel])} />}
        <div className="min-h-0 flex-1">
          {etat?.chargement && <div className="grid h-full place-items-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>}
          {etat?.erreur && <p className="text-sm text-red-600" data-testid="apercu-erreur">{etat.erreur}</p>}
          {etat?.pdf && <iframe src={etat.pdf} title={fichier?.nom} className="h-full w-full rounded-md border border-slate-200" data-testid="apercu-pdf" />}
          {etat?.image && <img src={etat.image} alt={fichier?.nom} className="mx-auto max-h-full max-w-full rounded-md border border-slate-200 object-contain" data-testid="apercu-image" />}
          {t && <TableApercu t={t} items={items} q={q.trim()} onOuvrir={ouvrir} onAssocier={setAssocier} sel={sel} onCoche={coche} />}
        </div>
        {fichier?.telechargeable && (
          <div className="flex justify-end">
            <Button variant="outline" size="sm" onClick={() => onDownload(fichier)} data-testid="apercu-telecharger"><Download className="mr-1.5 h-4 w-4" />Télécharger</Button>
          </div>
        )}
      </DialogContent>
      <AssocierDepotDialog resaIds={associer} onClose={() => setAssocier(null)} onDone={() => { setAssocier(null); setSel(new Set()); setVersion((v) => v + 1); }} />
    </Dialog>
  );
};
